
import { computed, defineComponent, onBeforeMount, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// types
import type { InvoiceT, CourierAppDeliveryCancelReason } from '@/core/types/orders.types'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'

type PayloadInvoicePlace = {
    placeId: number
    statusId: number
    cancelReasonId: number | null
}

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        Loader,
        TheInput,
        TheSelect,
        FileReader,
        Checkbox
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        const invoiceId = Number(route.params.invoice_id)

        const notifications = useNotifications()

        const isLoading = ref(false)

        const state = reactive({
            deliveredAt: null as string | null,
            receiverName: null as string | null,
            attachments: [] as File[],
            invoicePlaces: [] as PayloadInvoicePlace[]
        })

        const state_form$ = useVuelidate(
            {
                deliveredAt: { required },
                attachments: { required },
                receiverName: { required },
            },
            state
        )

        const invoice_state = reactive({
            is_loading: false,
            is_failed: false,
            invoice: null as InvoiceT | null,
        })

        const settings_state = reactive({
            is_loading: false,
            is_failed: false,
            is_partial_return_enabled: false,
        })

        const cancel_reasons_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as CourierAppDeliveryCancelReason[]
        })

        const loadCancelReasons = async () => {
            if (!invoice_state.invoice) return

            try {
                cancel_reasons_state.is_loading = true

                const response = await orders.getCancelReasons(invoice_state.invoice.company_id)
                cancel_reasons_state.options = response.data

                cancel_reasons_state.is_failed = false
            } catch (error) {
                console.error(error)
                cancel_reasons_state.is_failed = true
            } finally {
                cancel_reasons_state.is_loading = false
            }
        }

        const loadCompanySettings = async () => {
            if (!invoice_state.invoice) return

            try {
                settings_state.is_loading = true

                const response = await companies.getCompanySettings(invoice_state.invoice.company_id, { page: 1, limit: 9999 })
                const partialReturn = response.data.find((s) => s.key === 'partial_return')

                if (partialReturn) {
                    settings_state.is_partial_return_enabled = partialReturn.isActive
                }

                if (settings_state.is_partial_return_enabled) {
                    loadCancelReasons()

                    state.invoicePlaces = invoice_state.invoice.invoice_places.map((p) => {
                        return {
                            placeId: p.id,
                            statusId: 10,
                            cancelReasonId: null
                        }
                    })
                }

                settings_state.is_failed = false
            } catch (error) {
                console.error(error)
                settings_state.is_failed = true
            } finally {
                settings_state.is_loading = false
            }
        }

        const loadInvoice = async () => {
            try {
                invoice_state.is_loading = true

                const response = await orders.getInvoice(invoiceId)
                invoice_state.invoice = response.data

                invoice_state.is_failed = false

                await loadCompanySettings()
            } catch (error) {
                console.error(error)
                invoice_state.is_failed = true
            } finally {
                invoice_state.is_loading = false
            }
        }

        onBeforeMount(loadInvoice)

        const toggleStatus = (placeIndex: number) => {
            if (isLoading.value) return

            if (state.invoicePlaces[placeIndex]) {
                if (state.invoicePlaces[placeIndex].statusId === 10) {
                    state.invoicePlaces[placeIndex].statusId = 51
                } else {
                    state.invoicePlaces[placeIndex].statusId = 10
                    state.invoicePlaces[placeIndex].cancelReasonId = null
                }
            }
        }

        const onChangeCancelReason = (placeIndex: number, cancelReasonId: number) => {
            if (isLoading.value) return
            state.invoicePlaces[placeIndex].cancelReasonId = cancelReasonId
        }

        const onSubmit = async () => {
            if (isLoading.value) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid || !state.deliveredAt) return

            if (state.invoicePlaces.some((p) => p.statusId === 51 && !p.cancelReasonId)) {
                alert('Проставьте причину отмены для всех отмененных мест')
                return
            }

            try {
                isLoading.value = true

                const deliveredAt = state.deliveredAt.split('T').join(' ') + ':00'

                const formData = new FormData()
                formData.append('deliveredAt', deliveredAt)
                formData.append('receiverName', state.receiverName as string)

                state.attachments.forEach((attachment) => {
                    formData.append(`attachments[]`, attachment)
                })

                formData.append('invoicePlaces', JSON.stringify(state.invoicePlaces))

                await orders.approveDelivery(invoiceId, formData)

                notifications.notify({ title: 'Доставка закрыта', type: 'success' })
                router.replace(`/invoice/${invoiceId}`)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При закрытии доставки прозиошла ошибка',
                })
            } finally {
                isLoading.value = false
            }
        }

        const isAlreadyClosed = computed(() => {
            if (invoice_state.invoice) {
                if (invoice_state.invoice.status.code === 10 || invoice_state.invoice.status.code === 51) {
                    return true
                }
            }

            return false
        })

        const title = computed(() => {
            if (invoice_state.invoice) {
                return `Накладная ${invoice_state.invoice.invoice_number} | Проставить статус 'Доставлено'`
            }

            return `Проставить статус 'Доставлено'`
        })

        return {
            invoiceId,
            isLoading,
            state,
            state_form$,
            invoice_state,
            settings_state,
            cancel_reasons_state,
            loadCancelReasons,
            loadInvoice,
            loadCompanySettings,
            toggleStatus,
            onChangeCancelReason,
            onSubmit,
            isAlreadyClosed,
            title,
        }
    }
})
